import { collection, addDoc, query, where, getDocs, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

interface SongHistory {
  id?: string;
  songId: string;
  title: string;
  artist?: string;
  videoId?: string;
  source: 'youtube' | 'local';
  singerName: string;
  timestamp: Date | any;
}

/**
 * Singer History Service - tracks songs sung by each singer
 * Uses localStorage for guests, Firestore for registered users
 */
class SingerHistoryService {
  private STORAGE_KEY = 'singerHistory';
  private MAX_LOCAL_HISTORY = 50; // Keep last 50 songs in localStorage

  /**
   * Add a song to singer's history
   */
  async addToHistory(
    song: {
      id: string;
      title: string;
      artist?: string;
      videoId?: string;
      source: 'youtube' | 'local';
    },
    singerName: string,
    userId?: string
  ) {
    const historyItem: SongHistory = {
      songId: song.id,
      title: song.title,
      artist: song.artist || 'Unknown Artist',
      videoId: song.videoId,
      source: song.source,
      singerName,
      timestamp: new Date()
    };

    // Save to localStorage (for all users)
    this.addToLocalHistory(historyItem);

    // Also save to Firestore if user is registered
    if (userId) {
      try {
        // Filter out any remaining undefined values to prevent Firestore errors
        const firestoreData: Record<string, any> = { ...historyItem, userId, timestamp: serverTimestamp() };
        Object.keys(firestoreData).forEach(key => {
          if (firestoreData[key] === undefined) delete firestoreData[key];
        });
        await addDoc(collection(db, 'singer_history'), firestoreData);
      } catch (error) {
        console.error('Failed to save history to Firestore:', error);
      }
    }
  }

  /**
   * Get singer's history
   */
  async getHistory(userId?: string, limitCount: number = 20): Promise<SongHistory[]> {
    if (userId) {
      // Get from Firestore for registered users
      return this.getFirestoreHistory(userId, limitCount);
    } else {
      // Get from localStorage for guests
      return this.getLocalHistory(limitCount);
    }
  }

  /**
   * Get unique favorite songs (most sung)
   */
  async getFavorites(userId?: string, limitCount: number = 10) {
    const history = await this.getHistory(userId, 100);
    
    // Count song occurrences
    const songCounts: Record<string, { count: number; song: SongHistory }> = {};
    
    history.forEach(item => {
      const key = item.songId;
      if (!songCounts[key]) {
        songCounts[key] = { count: 0, song: item };
      }
      songCounts[key].count++;
    });

    // Sort by count and return top songs
    return Object.values(songCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, limitCount)
      .map(({ count, song }) => ({ ...song, timesPerformed: count }));
  }

  /**
   * Clear all history
   */
  async clearHistory(userId?: string) {
    // Clear localStorage
    localStorage.removeItem(this.STORAGE_KEY);

    // Clear Firestore if user is registered
    if (userId) {
      try {
        const historyRef = collection(db, 'singer_history');
        const q = query(historyRef, where('userId', '==', userId));
        const snapshot = await getDocs(q);
        
        // Note: Batch delete would be better, but this works for now
        const deletePromises = snapshot.docs.map(doc => doc.ref.delete());
        await Promise.all(deletePromises);
      } catch (error) {
        console.error('Failed to clear Firestore history:', error);
      }
    }
  }

  // Private methods for localStorage management

  private addToLocalHistory(item: SongHistory) {
    try {
      const history = this.getLocalHistory();
      
      // Add new item at the beginning
      history.unshift(item);
      
      // Keep only the most recent items
      const trimmed = history.slice(0, this.MAX_LOCAL_HISTORY);
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trimmed));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  private getLocalHistory(limitCount?: number): SongHistory[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const history: SongHistory[] = JSON.parse(stored);
      
      // Convert timestamp strings back to Dates
      history.forEach(item => {
        if (typeof item.timestamp === 'string') {
          item.timestamp = new Date(item.timestamp);
        }
      });
      
      return limitCount ? history.slice(0, limitCount) : history;
    } catch (error) {
      console.error('Failed to read from localStorage:', error);
      return [];
    }
  }

  private async getFirestoreHistory(userId: string, limitCount: number): Promise<SongHistory[]> {
    try {
      const historyRef = collection(db, 'singer_history');
      const q = query(
        historyRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          songId: data.songId,
          title: data.title,
          artist: data.artist,
          videoId: data.videoId,
          source: data.source,
          singerName: data.singerName,
          timestamp: data.timestamp?.toDate() || new Date()
        };
      });
    } catch (error) {
      console.error('Failed to get Firestore history:', error);
      // Fallback to localStorage
      return this.getLocalHistory(limitCount);
    }
  }

  /**
   * Search through history
   */
  async searchHistory(searchTerm: string, userId?: string): Promise<SongHistory[]> {
    const history = await this.getHistory(userId, 100);
    const term = searchTerm.toLowerCase();
    
    return history.filter(item => 
      item.title.toLowerCase().includes(term) ||
      (item.artist && item.artist.toLowerCase().includes(term))
    );
  }
}

export default new SingerHistoryService();
